import React, { useMemo, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { Badge } from '../../../components/ui/badge'
import { riskBandStyle, resolvedRiskLevel, formatRiskScoreNumber } from '../../../components/patron/riskScoreMeta'
import { buildPatronRiskDoughnutData } from '../../../lib/patronRiskBreakdown'

function DoughnutTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  const pct = row.percent != null ? `${row.percent}%` : null
  return (
    <div className="rounded-lg border bg-white px-3 py-2 shadow-md text-sm max-w-xs">
      <p className="font-semibold text-gray-900">{row.name}</p>
      <p className="text-gray-600">
        {row.value?.toFixed?.(2) ?? row.value} pts toward score
        {pct ? ` · ${pct} of ring` : ''}
      </p>
      {row.isBaseline && (
        <p className="text-xs text-gray-500 mt-1">Residual from clean component bands and rounding</p>
      )}
    </div>
  )
}

export default function PatronRiskDoughnut({
  riskScore,
  riskLevel,
  components,
  weights,
  riskFactors,
  loading = false,
  selectedDomainId,
  onSelectDomain,
  className = '',
}) {
  const [activeIndex, setActiveIndex] = useState(null)

  const chart = useMemo(
    () =>
      buildPatronRiskDoughnutData({
        components,
        weights,
        riskScore,
        riskFactors,
      }),
    [components, weights, riskScore, riskFactors]
  )

  const scoreNum = Number(riskScore)
  const level = resolvedRiskLevel(scoreNum, riskLevel)
  const band = riskBandStyle(scoreNum, level)
  const displayScore = formatRiskScoreNumber(scoreNum)

  const pieData = useMemo(() => {
    const total = chart.slices.reduce((s, x) => s + x.value, 0) || 1
    return chart.slices.map((s) => ({
      ...s,
      percent: Math.round((s.value / total) * 100),
    }))
  }, [chart.slices])

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-[240px] text-sm text-gray-500 ${className}`}>
        Loading score composition…
      </div>
    )
  }

  if (!pieData.length) {
    return (
      <div className={`flex flex-col items-center justify-center h-[240px] text-center px-4 ${className}`}>
        <p className="text-3xl font-bold text-gray-900">{displayScore}</p>
        {level && (
          <Badge className="mt-1 border" style={{ color: band.color, backgroundColor: band.bg, borderColor: band.border }}>
            {level}
          </Badge>
        )}
        <p className="text-xs text-gray-500 mt-3">Recalculate to show score composition</p>
      </div>
    )
  }

  return (
    <div className={`flex flex-col md:flex-row gap-4 items-center md:items-start ${className}`}>
      <div className="relative w-[240px] h-[240px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={72}
              outerRadius={100}
              paddingAngle={2}
              activeIndex={activeIndex}
              onMouseEnter={(_, i) => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={(_, i) => {
                const id = pieData[i]?.id
                if (id && onSelectDomain) onSelectDomain(selectedDomainId === id ? null : id)
              }}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={entry.id}
                  fill={entry.color}
                  stroke={selectedDomainId === entry.id ? '#1e293b' : '#fff'}
                  strokeWidth={selectedDomainId === entry.id ? 2 : 1}
                  opacity={entry.isBaseline ? 0.55 : 1}
                  style={{ cursor: onSelectDomain ? 'pointer' : 'default' }}
                />
              ))}
            </Pie>
            <Tooltip content={<DoughnutTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-3xl font-bold text-gray-900 leading-none">{displayScore}</span>
          <span className="text-xs text-gray-500 mt-0.5">/ 100</span>
          {level && (
            <Badge
              className="mt-2 text-xs border"
              style={{ color: band.color, backgroundColor: band.bg, borderColor: band.border }}
            >
              {level}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0 w-full">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Score composition</p>
        <ul className="space-y-2">
          {pieData.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelectDomain?.(selectedDomainId === s.id ? null : s.id)}
                className={`w-full flex items-center justify-between gap-2 text-left text-sm rounded-md px-2 py-1.5 transition-colors ${
                  selectedDomainId === s.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color, opacity: s.isBaseline ? 0.6 : 1 }} />
                  <span className="truncate text-gray-800">{s.name}</span>
                </span>
                <span className="shrink-0 tabular-nums text-gray-600">
                  {s.value.toFixed(1)} <span className="text-gray-400">({s.percent}%)</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        {chart.source === 'risk_factors' && (
          <p className="text-xs text-amber-700 mt-3 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
            Estimated from saved factors — use Recalculate for exact composition.
          </p>
        )}
        {chart.hasDefaultEstimate && (
          <p className="text-xs text-gray-500 mt-2">Includes default-risk penalties where profile data is missing.</p>
        )}
      </div>
    </div>
  )
}
